import React, { useState } from "react";
import {
  View,
  Image,
  ScrollView,
  Pressable,
  Modal,
  StatusBar,
  TouchableWithoutFeedback,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { X, ChevronLeft, ChevronRight } from "lucide-react-native";
import type { Clinic } from "@louise/clinics/clinics.types";
import {
  getClinicFacilityImages,
  getClinicDisplayImageUrl,
  type ClinicDisplayImage,
} from "./clinicImagePolicy";

import { usePressCapture } from "@src/packages/analytics/usePressCapture";
function getProviderImageUrl(
  image: ReturnType<typeof getClinicFacilityImages>[number] | undefined,
) {
  return image ? getClinicDisplayImageUrl(image) : null;
}

type Props = {
  clinic: Clinic;
};

export default function ClinicImageGallery({ clinic }: Props) {
  const capturePress = usePressCapture("ClinicImageGallery");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const insets = useSafeAreaInsets();

  const images = getClinicFacilityImages(clinic.images as ClinicDisplayImage[]);

  if (images.length === 0) return null;
  const previewUrl =
    previewIndex !== null ? getProviderImageUrl(images[previewIndex]) : null;

  return (
    <>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 12, paddingHorizontal: 4 }}
        decelerationRate="fast"
      >
        {images.map((image, index) => {
          const imageUrl = getProviderImageUrl(image);
          return (
            <Pressable
              key={`${image.url}-${index}`}
              onPress={() => {
                // Image thumbnail with no visible copy to name it.
                capturePress("open_clinic_image");
                setPreviewIndex(index);
              }}
              style={{
                width: 280,
                height: 190,
                borderRadius: 20,
                overflow: "hidden",
                borderWidth: 1,
                borderColor: "rgba(255,255,255,0.4)",
              }}
            >
              {imageUrl ? (
                <Image
                  source={{ uri: imageUrl }}
                  style={{ width: "100%", height: "100%" }}
                  resizeMode="cover"
                />
              ) : (
                <View
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255,255,255,0.15)",
                  }}
                />
              )}
            </Pressable>
          );
        })}
      </ScrollView>

      {/* Full-screen preview modal */}
      <Modal
        visible={previewIndex !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setPreviewIndex(null)}
      >
        <StatusBar backgroundColor="black" barStyle="light-content" />
        <TouchableWithoutFeedback onPress={() => setPreviewIndex(null)}>
          <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.95)" }}>
            {previewUrl && (
              <Image
                source={{ uri: previewUrl }}
                style={{ flex: 1 }}
                resizeMode="contain"
              />
            )}
            {/* Close button */}
            <Pressable
              onPress={() => {
                // X-icon with no visible copy to name it.
                capturePress("close_clinic_image");
                setPreviewIndex(null);
              }}
              style={{
                position: "absolute",
                top: insets.top + 12,
                right: 16,
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: 20,
                padding: 8,
              }}
            >
              <X size={20} color="white" />
            </Pressable>

            {/* Prev / dots / next */}
            {images.length > 1 && previewIndex !== null && (
              <View
                style={{
                  position: "absolute",
                  bottom: insets.bottom + 24,
                  left: 0,
                  right: 0,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                }}
              >
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    // Chevron-only control with no visible copy to name it.
                    capturePress("previous_clinic_image");
                    setPreviewIndex((i) => (i !== null && i > 0 ? i - 1 : i));
                  }}
                  style={{ opacity: previewIndex > 0 ? 1 : 0.2 }}
                >
                  <ChevronLeft size={20} color="white" />
                </Pressable>

                <View
                  style={{
                    flexDirection: "row",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  {images.map((_, i) => (
                    <Pressable
                      key={i}
                      onPress={(e) => {
                        e.stopPropagation();
                        // Dot indicator with no visible copy to name it.
                        capturePress("jump_to_clinic_image");
                        setPreviewIndex(i);
                      }}
                    >
                      <View
                        style={{
                          width: i === previewIndex ? 20 : 6,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor:
                            i === previewIndex
                              ? "white"
                              : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </Pressable>
                  ))}
                </View>

                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    // Chevron-only control with no visible copy to name it.
                    capturePress("next_clinic_image");
                    setPreviewIndex((i) =>
                      i !== null && i < images.length - 1 ? i + 1 : i,
                    );
                  }}
                  style={{
                    opacity: previewIndex < images.length - 1 ? 1 : 0.2,
                  }}
                >
                  <ChevronRight size={20} color="white" />
                </Pressable>
              </View>
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
}
